import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import adsData from "../data/ads.json";
import type { AdsConfig, SingleAdConfig } from "./AdTypes";

interface Props {
  slot: string;
}

// const DEBUG_ADS = process.env.NODE_ENV === "development";
const DEBUG_ADS = false; // 👈 ALWAYS ENABLE (dev + prod)


const AdRenderer = ({ slot }: Props) => {
  const { pathname } = useLocation(); // 👈 route tracking
  const config = adsData as AdsConfig;
  const ad: SingleAdConfig | undefined = config.ads[slot];
  const adRef = useRef<HTMLDivElement>(null);

  // ❌ slot disabled / missing
  if (!ad || !ad.enabled) {
    if (DEBUG_ADS) {
      console.warn(`[ADS] Slot "${slot}" not found or disabled`);
    }
    return null;
  }

  useEffect(() => {
    if (!adRef.current) return;

    if (DEBUG_ADS) {
      console.log(`[ADS] Route changed → ${pathname}`);
      console.log(`[ADS] Rebuilding ad slot → ${slot}`);
    }

    // 🧨 REMOVE old ad completely
    adRef.current.innerHTML = "";

    // ✅ CREATE brand new <ins> every time
    const ins = document.createElement("ins");
    ins.className = "adsbygoogle";
    ins.style.display = "block";

    ins.setAttribute("data-ad-client", config.publisherId);
    ins.setAttribute("data-ad-slot", ad.slot);
    ins.setAttribute("data-ad-format", ad.format);

    if (ad.layout) {
      ins.setAttribute("data-ad-layout", ad.layout);
    }

    if (ad.layoutKey) {
      ins.setAttribute("data-ad-layout-key", ad.layoutKey);
    }

    if (ad.responsive) {
      ins.setAttribute("data-full-width-responsive", "true");
    }

    adRef.current.appendChild(ins);

    const timer = setTimeout(() => {
      try {
        if (DEBUG_ADS) {
          console.log(`[ADS] adsbygoogle.push() → ${slot}`);
        }

        // @ts-ignore
        (window.adsbygoogle = window.adsbygoogle || []).push({});

        if (DEBUG_ADS) {
          console.log(
            `%c[ADS] SUCCESS → ${slot}`,
            "color: green; font-weight: bold"
          );
        }
      } catch (e) {
        console.error(`[ADS] Adsense error → ${slot}`, e);
      }
    }, 300); // small delay is REQUIRED for SPA

    return () => {
      clearTimeout(timer);
      if (DEBUG_ADS) {
        console.log(`[ADS] Cleanup effect → ${slot}`);
      }
    };
  }, [pathname, slot]);

  return (
    <div
      ref={adRef}
      style={ad.minHeight ? { minHeight: ad.minHeight } : undefined}
      className="w-full overflow-hidden"
    />
  );
};

export default AdRenderer;
